import React from 'react';

/**
 * Sélecteur rapide de période (Tout / Aujourd'hui / Cette semaine / Ce mois).
 *
 * Props :
 *  - value: 'all' | 'today' | 'week' | 'month'
 *  - onChange: (newValue) => void
 *  - color: 'green' | 'red' | 'blue' | 'indigo' (palette par défaut 'blue')
 *  - testIdPrefix: préfixe pour data-testid (ex. 'enc-period' → 'enc-period-all')
 */
const PALETTES = {
  green: { border: 'border-green-200', text: 'text-green-700', hover: 'hover:bg-green-50', active: 'bg-green-600 text-white' },
  red: { border: 'border-red-200', text: 'text-red-700', hover: 'hover:bg-red-50', active: 'bg-red-600 text-white' },
  blue: { border: 'border-blue-200', text: 'text-blue-700', hover: 'hover:bg-blue-50', active: 'bg-blue-600 text-white' },
  indigo: { border: 'border-indigo-200', text: 'text-indigo-700', hover: 'hover:bg-indigo-50', active: 'bg-indigo-600 text-white' },
  gray: { border: 'border-gray-200', text: 'text-gray-700', hover: 'hover:bg-gray-50', active: 'bg-gray-700 text-white' },
};

const OPTIONS = [
  { k: 'all', l: 'Tout' },
  { k: 'today', l: "Aujourd'hui" },
  { k: 'week', l: 'Cette semaine' },
  { k: 'month', l: 'Ce mois' },
];

export default function PeriodFilter({ value = 'all', onChange, color = 'blue', testIdPrefix = 'period' }) {
  const p = PALETTES[color] || PALETTES.blue;
  return (
    <div className={`inline-flex bg-white border rounded-md overflow-hidden text-xs ${p.border}`} data-testid={`${testIdPrefix}-filter`}>
      {OPTIONS.map(opt => (
        <button
          key={opt.k}
          type="button"
          onClick={() => onChange?.(opt.k)}
          className={`px-3 py-1 font-medium transition-colors ${value === opt.k ? p.active : `${p.text} ${p.hover}`}`}
          data-testid={`${testIdPrefix}-${opt.k}`}
        >
          {opt.l}
        </button>
      ))}
    </div>
  );
}

/**
 * Filtre une liste d'éléments par période, en se basant sur le champ `date` (string YYYY-MM-DD).
 */
export function applyPeriodFilter(rows, period, dateField = 'date') {
  if (!Array.isArray(rows) || period === 'all') return rows;
  const now = new Date();
  const todayStr = now.toISOString().split('T')[0];
  if (period === 'today') return rows.filter(r => (r[dateField] || '') === todayStr);
  if (period === 'week') {
    const d = new Date(now); const day = d.getDay();
    const diff = (day === 0 ? -6 : 1 - day);
    d.setDate(d.getDate() + diff); d.setHours(0, 0, 0, 0);
    const start = d.toISOString().split('T')[0];
    return rows.filter(r => (r[dateField] || '') >= start && (r[dateField] || '') <= todayStr);
  }
  if (period === 'month') {
    const ym = todayStr.slice(0, 7);
    return rows.filter(r => (r[dateField] || '').startsWith(ym));
  }
  return rows;
}

/**
 * Convertit une valeur PeriodFilter en {date_debut, date_fin} (YYYY-MM-DD).
 * Renvoie {} pour 'all' (toute la période).
 */
export function periodToRange(period) {
  if (!period || period === 'all') return {};
  const now = new Date();
  const today = now.toISOString().split('T')[0];
  if (period === 'today') return { date_debut: today, date_fin: today };
  if (period === 'week') {
    const d = new Date(now); const day = d.getDay();
    d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
    return { date_debut: d.toISOString().split('T')[0], date_fin: today };
  }
  if (period === 'month') {
    const y = now.getFullYear(); const m = now.getMonth() + 1;
    const start = `${y}-${String(m).padStart(2, '0')}-01`;
    const lastDay = new Date(y, m, 0).getDate();
    return { date_debut: start, date_fin: `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}` };
  }
  return {};
}
